import { PureComponent } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import uuid from 'uuid/v4';
import styled from 'styled-components';
import { CircularProgress } from '@material-ui/core';
import URI from 'urijs';
import { getSignedUrl as fetchSignedUrl, SignedUrlObject } from 'shared/api/s3';
import { uploadFileWithSignedUrl } from 'shared/util/s3Upload.util';
import { withRouter } from 'react-router-dom';
import config from 'builder/config';
import SiteEditContext from 'shared/util/SiteEditContext';
import { UploadedFile } from 'shared/widgets/shared/types';
import { UploadButton } from '../S3Image/S3Image';
import { ArrowUpwardIcon } from '@uhg-harmony/uitk-icons-react';
import {
  S3UploadFileProps,
  S3UploadFileState,
  Wrapper,
  UploadLabel,
} from '../S3UploadFile/S3UploadFile';
import { getTemplateSiteID } from '../../scenes/PageTemplates/utils';

const InputWrapper = styled.div`
  & .MuiButton-root {
    font-family: OptumSans, sans-serif;
    font-weight: 700;
  }
  display: flex;
  flex-direction: row;
  width: 100%;
  > label {
    width: unset;
  }
`;

const StyledUploadButton = styled(UploadButton)`
  font-family: OptumSans, sans-serif;
`;

class S3UploadFileButton extends PureComponent<
  S3UploadFileProps,
  S3UploadFileState
> {
  public static readonly contextType = SiteEditContext;

  constructor(props: S3UploadFileProps) {
    super(props);
    this.state = {
      uploading: false,
      uuid: uuid(),
    };
  }

  handleFinish = (signedUrlObject: SignedUrlObject) => {
    const { onUpload } = this.props;
    this.setState({
      uploading: false,
    });
    const uri = URI(signedUrlObject.signedUrl);
    const file: UploadedFile = {
      url: uri
        .hostname(config.cdn)
        .query('')
        .toString(),
      name: uri.filename().toString(),
    };
    onUpload(file);
  };

  rejectUpload = () => {
    this.setState({
      uploading: false,
    });
    toast('This file type is not supported', {
      type: 'error',
      theme: 'colored',
    });
  };

  onUploadStart(file: File, callback: any) {
    const fileName = file.name;
    const { fileFormatRegex } = this.props;
    // If extension of file being uploaded is as per fileformatRegex then do callback otherwise reject the upload.
    fileFormatRegex &&
      (fileFormatRegex.test(fileName) ? callback(file) : this.rejectUpload());
  }

  render() {
    const { uploading, uuid } = this.state;
    const {
      label,
      accept,
      match: {
        params: { siteId },
        path,
      },
    } = this.props;
    const siteUploadId = getTemplateSiteID(siteId, path);
    return (
      <Wrapper>
        <input
          id={uuid}
          type="file"
          style={{ display: 'none' }}
          accept={accept}
          data-test-id={`file-uploader`}
          onChange={async e => {
            const selected = e.target.files?.[0];
            if (!selected) return;
            // Validate file type by filename before starting upload
            if (
              this.props.fileFormatRegex &&
              !this.props.fileFormatRegex.test(selected.name)
            ) {
              toast('Invalid File Type', { type: 'error', theme: 'colored' });
              return;
            }
            this.onUploadStart(selected, async () => {
              try {
                this.setState({ uploading: true });
                const signed = await uploadFileWithSignedUrl(
                  selected,
                  (f, cb) => fetchSignedUrl(f, cb, siteUploadId),
                  {
                    'Content-Type': selected.type || 'application/octet-stream',
                  }
                );
                this.handleFinish(signed);
              } catch (err) {
                this.setState({ uploading: false });
                // eslint-disable-next-line no-console
                console.error('Error uploading file', err);
                toast('Error uploading file', {
                  type: 'error',
                  theme: 'colored',
                });
              }
            });
          }}
        />
        <InputWrapper>
          <UploadLabel htmlFor={uuid}>
            <StyledUploadButton
              style={{ textTransform: 'none' }}
              size="medium"
              variant="outlined"
              startIcon={
                uploading ? <CircularProgress size={15} /> : <ArrowUpwardIcon />
              }
            >
              {label}
            </StyledUploadButton>
          </UploadLabel>
        </InputWrapper>
      </Wrapper>
    );
  }
}

export { S3UploadFileButton };
export default withRouter(S3UploadFileButton);
